import React, { Component, ReactElement, Suspense } from 'react';
import { Link, Redirect, Route, RouteComponentProps, Switch, withRouter } from 'react-router-dom';
import { WEBSHOP_ENABLED } from '../../constants/Settings';
import { ToastContainer } from '../../components/Toast/ToastContainer';
import { retryLazy } from 'react-retry-lazy';
import { t } from 'i18next';
import { Alert } from '@mui/material';
import { Header } from '../../components/Header/Header';
import { DeactivatedAccountBanner } from '../../components/DeactivatedAccountBanner/DeactivatedAccountBanner';
import Footer from '../../components/Footer/Footer';
import { DSGVOOverlay } from '../../components/DSGVO/DSGVOOverlay';
import { ScrollToTop } from '../../components/ScrollTop/ScrollTop';
import { geolocationService } from '../../services/geolocation-service';
import { regionStore } from '../../store/jotai/store';
import { userAtom } from '../../store/jotai/atoms/user.atom';
import { getFirstVendor } from '../../utils/userUtils';
import './Wrapper.scss';

const Product = retryLazy(
  () =>
    import('../Product/Product').then((m) => ({
      default: m.Product
    })),
  'Product'
);
const Checkout = retryLazy(() => import('../Checkout/Checkout'), 'Checkout');
const Categories = retryLazy(() => import('../Categories/categories'), 'Categories');
const CheckoutPayment = retryLazy(
  () => import('../Checkout/CheckoutPayment/CheckoutPayment'),
  'CheckoutPayment'
);
const CheckoutSuccess = retryLazy(
  () => import('../Checkout/CheckoutSuccess/CheckoutSuccess'),
  'CheckoutSuccess'
);
// NotFound has no default export, so map the named one onto `default`.
const NotFound = retryLazy(
  () => import('../../components/NotFound/NotFound').then((m) => ({ default: m.NotFound })),
  'NotFound'
);
const RegionPage = retryLazy(
  () => import('../RegionPage/RegionPage').then((m) => ({ default: m.RegionPage })),
  'RegionPage'
);
const Marketplace = retryLazy(() => import('../Marketplace/Marketplace'), 'Marketplace');
const VendorOverview = retryLazy(
  () => import('../VendorOverview/VendorOverview').then((m) => ({ default: m.VendorOverview })),
  'VendorOverview'
);
const SearchPage = retryLazy(
  () => import('../SearchPage/SearchPage').then((m) => ({ default: m.SearchPage })),
  'SearchPage'
);
const Login = retryLazy(
  () => import('../Login/Login').then((m) => ({ default: m.Login })),
  'Login'
);
const ResetPassword = retryLazy(
  () => import('../ResetPassword/ResetPassword').then((m) => ({ default: m.ResetPassword })),
  'ResetPassword'
);
const ConfirmEmailChange = retryLazy(
  () =>
    import('../ConfirmEmailChange/ConfirmEmailChange').then((m) => ({
      default: m.ConfirmEmailChange
    })),
  'ConfirmEmailChange'
);
const RegionNewsPage = retryLazy(
  () =>
    import('../RegionPage/RegionNewsPage').then((m) => ({
      default: m.RegionNewsPage
    })),
  'RegionNewsPage'
);
const RegionPostPage = retryLazy(
  () =>
    import('../RegionPage/RegionPostPage').then((m) => ({
      default: m.RegionPostPage
    })),
  'RegionPostPage'
);
const RegionServicePage = retryLazy(
  () => import('../RegionPage/RegionServicePage'),
  'RegionServicePage'
);
const RegionJobPage = retryLazy(
  () =>
    import('../RegionPage/RegionJobPage').then((m) => ({
      default: m.RegionJobPage
    })),
  'RegionJobPage'
);
const VendorRegisterForm = retryLazy(
  () => import('../Register/VendorRegisterForm'),
  'VendorRegisterForm'
);
const CustomerRegister = retryLazy(
  () =>
    import('../Register/CustomerRegister').then((m) => ({
      default: m.CustomerRegister
    })),
  'CustomerRegister'
);
const MyAccount = retryLazy(
  () => import('../MyAccount/MyAccount').then((m) => ({ default: m.MyAccount })),
  'MyAccount'
);
const RegionEventPage = retryLazy(
  () =>
    import('../RegionPage/RegionEventPage').then((m) => ({
      default: m.RegionEventPage
    })),
  'RegionEventPage'
);
const VendorHowToPage = retryLazy(
  () => import('../VendorHowToPage/VendorHowToPage'),
  'VendorHowToPage'
);
const VendorDetail = retryLazy(
  () => import('../Vendor/VendorDetail').then((m) => ({ default: m.VendorDetail })),
  'VendorDetail'
);
const Register = retryLazy(
  () => import('../Register/Register').then((m) => ({ default: m.Register })),
  'Register'
);
const Home = retryLazy(() => import('../Home/Home'), 'Home');
const Page = retryLazy(() => import('../Page/Page'), 'Page');

interface WrapperProps extends RouteComponentProps {}

interface WrapperState {
  vendorNeedsPlan: boolean;
}

class Wrapper extends Component<WrapperProps, WrapperState> {
  private unsubUser: (() => void) | null = null;

  constructor(props: WrapperProps) {
    super(props);

    this.state = {
      vendorNeedsPlan: this.checkVendorNeedsPlan()
    };
  }

  componentDidMount() {
    geolocationService.reset();

    this.unsubUser = regionStore.sub(userAtom, () => {
      this.setState({ vendorNeedsPlan: this.checkVendorNeedsPlan() });
    });
  }

  componentWillUnmount() {
    this.unsubUser?.();
  }

  checkVendorNeedsPlan = (): boolean => {
    const user = regionStore.get(userAtom);
    if (!user) return false;
    const vendor = getFirstVendor(user);
    return !!vendor?.id && !vendor?.plan?.id;
  };

  render(): ReactElement {
    const { location } = this.props;
    const { vendorNeedsPlan } = this.state;
    const isVendorRegisterPage = location.pathname.startsWith('/händler-anmeldung');

    return (
      <>
        <ToastContainer />

        <ScrollToTop />

        <Header />

        <DeactivatedAccountBanner />

        {vendorNeedsPlan && !isVendorRegisterPage && (
          <Alert severity='warning' className='wrapper__plan-banner'>
            {t('vendorPlanBanner.text')}{' '}
            <Link to='/myaccount/vendorregistration' className='wrapper__plan-banner-link'>
              {t('vendorPlanBanner.link')}
            </Link>
          </Alert>
        )}

        <Suspense fallback={<div style={{ height: '100vh' }}></div>}>
          <div role='presentation' key={location.key}>
            <DSGVOOverlay />

            <Switch>
              <Route path='/login'>
                <Login />
              </Route>
              <Route path='/register'>
                <Register />
              </Route>
              <Route path='/reset-password'>
                <ResetPassword />
              </Route>
              {/* Opened from the link mailed to the new address; the token in
                  the query is what authorises it, so no session is needed. */}
              <Route path='/email-bestaetigen'>
                <ConfirmEmailChange />
              </Route>
              <Route path='/kategorien' component={Categories} />
              <Route path='/suche/' component={SearchPage} />
              {WEBSHOP_ENABLED && <Route path='/produkte/:id/:category' component={Marketplace} />}
              {WEBSHOP_ENABLED && <Route path='/produkte' component={Marketplace} />}
              {!WEBSHOP_ENABLED && <Redirect from='/produkte' to='/region' />}
              <Route path='/händlerübersicht' component={VendorOverview} />
              <Route path='/myaccount' component={MyAccount} />
              <Route path='/haendler/:urlsegment/' component={VendorDetail} />
              <Route path='/händler-werden/' component={VendorHowToPage} />
              <Route path='/händler-anmeldung/' component={VendorRegisterForm} />
              <Route path='/kunden-anmeldung/' component={CustomerRegister} />
              <Route path='/produkt/:urlsegment' component={Product} />
              <Route path='/region/' exact component={RegionPage} />
              <Route path='/region/:tab' exact component={RegionPage} />
              <Route path='/news/:urlsegment' component={RegionNewsPage} />
              <Route path='/services/:urlsegment' component={RegionServicePage} />
              <Route path='/jobs/:urlsegment' component={RegionJobPage} />
              <Route path='/events/:urlsegment' component={RegionEventPage} />
              <Route path='/posts/:urlsegment' component={RegionPostPage} />
              <Route exact path='/product-not-found'>
                <NotFound text={t('product.notFound')} />
              </Route>
              <Route exact path='/vendor-not-found'>
                <NotFound text={t('vendor.notFound')} />
              </Route>
              <Route exact path='/content/:urlsegment' component={Page} />
              {WEBSHOP_ENABLED && <Route exact path='/checkout' component={Checkout} />}
              {WEBSHOP_ENABLED && (
                <Route exact path='/checkout/payment' component={CheckoutPayment} />
              )}
              {WEBSHOP_ENABLED && (
                <Route exact path='/checkout/success' component={CheckoutSuccess} />
              )}
              {!WEBSHOP_ENABLED && <Redirect from='/checkout' to='/' />}
              <Route exact path='/'>
                <Home />
              </Route>
              <Route>
                <NotFound text={t('errors.page.notFound')} />
              </Route>
            </Switch>
          </div>
        </Suspense>

        <Footer />
      </>
    );
  }
}

export default withRouter(Wrapper);
